import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

const countryCodes = [
  { code: "20", name: "مصر", flag: "🇪🇬", digits: 10 },
  { code: "966", name: "السعودية", flag: "🇸🇦", digits: 9 },
  { code: "971", name: "الإمارات", flag: "🇦🇪", digits: 9 },
  { code: "965", name: "الكويت", flag: "🇰🇼", digits: 8 },
  { code: "974", name: "قطر", flag: "🇶🇦", digits: 8 },
  { code: "973", name: "البحرين", flag: "🇧🇭", digits: 8 },
  { code: "968", name: "عُمان", flag: "🇴🇲", digits: 8 },
  { code: "962", name: "الأردن", flag: "🇯🇴", digits: 9 },
  { code: "961", name: "لبنان", flag: "🇱🇧", digits: 8 },
  { code: "963", name: "سوريا", flag: "🇸🇾", digits: 9 },
  { code: "964", name: "العراق", flag: "🇮🇶", digits: 10 },
  { code: "967", name: "اليمن", flag: "🇾🇪", digits: 9 },
  { code: "970", name: "فلسطين", flag: "🇵🇸", digits: 9 },
  { code: "218", name: "ليبيا", flag: "🇱🇾", digits: 9 },
  { code: "216", name: "تونس", flag: "🇹🇳", digits: 8 },
  { code: "213", name: "الجزائر", flag: "🇩🇿", digits: 9 },
  { code: "212", name: "المغرب", flag: "🇲🇦", digits: 9 },
  { code: "249", name: "السودان", flag: "🇸🇩", digits: 9 },
  { code: "252", name: "الصومال", flag: "🇸🇴", digits: 8 },
  { code: "1", name: "أمريكا", flag: "🇺🇸", digits: 10 },
  { code: "44", name: "بريطانيا", flag: "🇬🇧", digits: 10 },
  { code: "33", name: "فرنسا", flag: "🇫🇷", digits: 9 },
  { code: "49", name: "ألمانيا", flag: "🇩🇪", digits: 11 },
];

export default function Login() {
  const { t } = useTranslation("login");
  const [countryCode, setCountryCode] = useState("20");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedCountry = countryCodes.find((c) => c.code === countryCode) || countryCodes[0];
  const maxDigits = selectedCountry.digits;
  const digitsLeft = maxDigits - phoneNumber.length;

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Remove all non-digit characters
    let cleaned = raw.replace(/[^0-9]/g, "");
    // Strip leading zeros — user already has country code
    cleaned = cleaned.replace(/^0+/, "");
    // Truncate to max digits for the selected country
    if (cleaned.length > maxDigits) {
      cleaned = cleaned.slice(0, maxDigits);
    }
    setPhoneNumber(cleaned);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!phoneNumber) {
      setError(t("login.error.phoneRequired"));
      setIsLoading(false);
      return;
    }

    if (phoneNumber.length < maxDigits) {
      setError(t("login.error.phoneIncomplete"));
      setIsLoading(false);
      return;
    }

    const fullPhone = countryCode + phoneNumber;
    const result = await login(fullPhone);
    setIsLoading(false);

    if (result.success) {
      navigate("/verify", { state: { phone: fullPhone } });
    } else {
      setError(result.error || t("login.error.sendFailed"));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-primary-100 flex items-center justify-center">
            <i className="ri-smartphone-line text-primary-600 text-2xl"></i>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-2">
            {t("login.title")}
          </h1>
          <p className="text-foreground-500 text-sm">
            {t("login.subtitle")}
          </p>
        </div>

        <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/50">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-2">
                {t("login.phoneLabel")}
              </label>
              <div className="flex items-stretch gap-2">
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="h-full flex items-center gap-1.5 px-3 py-3 rounded-xl bg-background-100 border border-background-200 text-foreground-950 text-sm hover:border-background-300 transition-colors cursor-pointer whitespace-nowrap shrink-0"
                  >
                    <span className="text-base">{selectedCountry.flag}</span>
                    <span className="font-medium">+{selectedCountry.code}</span>
                    <i className={`${dropdownOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-foreground-400 text-sm transition-transform`}></i>
                  </button>

                  {dropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-56 max-h-56 overflow-y-auto rounded-xl bg-background-50 border border-background-200 shadow-lg z-50 py-1">
                      {countryCodes.map((country) => (
                        <button
                          key={country.code}
                          type="button"
                          onClick={() => {
                            setCountryCode(country.code);
                            setPhoneNumber("");
                            setDropdownOpen(false);
                          }}
                          className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm cursor-pointer transition-colors whitespace-nowrap ${
                            countryCode === country.code
                              ? "bg-primary-50 text-primary-700 font-medium"
                              : "text-foreground-700 hover:bg-background-100"
                          }`}
                        >
                          <span className="text-base">{country.flag}</span>
                          <span className="flex-1 text-right">{country.name}</span>
                          <span className="text-foreground-400 text-xs tabular-nums">+{country.code} · {country.digits}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex-1 relative">
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder={t("login.phonePlaceholder")}
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-foreground-950 text-sm outline-none focus:border-primary-400 transition-colors placeholder:text-foreground-400"
                    required
                  />
                  {phoneNumber.length > 0 && (
                    <span
                      className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs tabular-nums font-medium ${
                        phoneNumber.length === maxDigits
                          ? "text-emerald-600"
                          : "text-foreground-400"
                      }`}
                    >
                      {phoneNumber.length}/{maxDigits}
                    </span>
                  )}
                </div>
              </div>
              <p className="text-foreground-400 text-xs mt-1.5">
                {t("login.phoneHint")}
              </p>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 rounded-lg px-3 py-2.5">
                <i className="ri-error-warning-line text-sm"></i>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary-500 text-white text-sm font-medium py-3 rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  {t("login.sendingOtp")}
                </>
              ) : (
                <>
                  {t("login.sendOtp")}
                  <i className="ri-arrow-right-line text-sm"></i>
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-foreground-500 text-sm mt-6">
          <Link to="/" className="text-primary-600 hover:text-primary-700 transition-colors underline">
            {t("login.backToHome")}
          </Link>
        </p>
      </div>
    </div>
  );
}